import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel, Stack, TextField, Typography } from '@mui/material';
import { CheckCircleOutline, EditOutlined, RestartAlt, WarningAmberOutlined } from '@mui/icons-material';
import type { PatientV2ReviewPendencyPayload, PatientV2ReviewSectionPayload } from '../../../../services/patients/patientsV2Service';
import type { PatientV2Detail } from './types';
import { BrighterGreen, DarkGreen } from '../../../../app/theme/tokens/colors';

export type DraftReviewSection = 'patient' | 'clinical';

type ReviewField = { field: string; label: string; value: (patient: PatientV2Detail) => string };
const patientFields: ReviewField[] = [
  { field: 'patient', label: 'Nome', value: (patient) => patient.patient },
  { field: 'sap', label: 'SAP', value: (patient) => patient.sap },
  { field: 'cpf', label: 'CPF', value: (patient) => patient.cpf },
  { field: 'birthDate', label: 'Data de nascimento', value: (patient) => patient.birthDate },
  { field: 'email', label: 'E-mail', value: (patient) => patient.email },
  { field: 'cellphone', label: 'Celular', value: (patient) => patient.cellphone },
  { field: 'phoneNumber', label: 'Telefone', value: (patient) => patient.phoneNumber },
  { field: 'cep', label: 'CEP', value: (patient) => patient.cep },
  { field: 'address', label: 'Endereço', value: (patient) => patient.address },
  { field: 'number', label: 'Número', value: (patient) => patient.number },
  { field: 'addressLine2', label: 'Complemento', value: (patient) => patient.addressLine2 },
  { field: 'neighborhood', label: 'Bairro', value: (patient) => patient.neighborhood },
  { field: 'city', label: 'Cidade', value: (patient) => patient.city },
  { field: 'uf', label: 'UF', value: (patient) => patient.uf },
  { field: 'companyId', label: 'CNPJ', value: (patient) => String(patient.companyId) },
];
const clinicalFields: ReviewField[] = [
  { field: 'cidPrimary', label: 'CID principal', value: (patient) => patient.clinical.cidPrimary },
  { field: 'cidSecondary', label: 'CID secundário', value: (patient) => patient.clinical.cidSecondary },
  { field: 'clinicalDiagnosis', label: 'Diagnóstico clínico', value: (patient) => patient.clinical.diagnosis },
  { field: 'oxygenIndication', label: 'Indicação de O₂', value: (patient) => patient.clinical.oxygenIndication },
  { field: 'prescribedOxygenSource', label: 'Fonte de O₂ prescrita', value: (patient) => patient.clinical.oxygenSource },
  { field: 'oxygenFlowLMin', label: 'Fluxo (L/min)', value: (patient) => patient.clinical.oxygenFlow },
  { field: 'dailyUseHours', label: 'Horas de uso/dia', value: (patient) => patient.clinical.dailyUse },
  { field: 'requestedEquipment', label: 'Equipamento solicitado', value: (patient) => patient.clinical.requestedEquipment },
  { field: 'requestingDoctorName', label: 'Médico solicitante', value: (patient) => patient.clinical.requestingDoctorName },
  { field: 'requestingDoctorCrm', label: 'CRM do médico', value: (patient) => patient.clinical.requestingDoctorCrm },
  { field: 'requestDate', label: 'Data da solicitação', value: (patient) => patient.clinical.requestDate },
];

const sectionConfig = (section: DraftReviewSection) =>
  section === 'clinical' ? { label: 'Informações clínicas', fields: clinicalFields } : { label: 'Dados do paciente', fields: patientFields };

interface Props {
  section: DraftReviewSection;
  patient: PatientV2Detail;
  value?: PatientV2ReviewSectionPayload;
  onChange: (value?: PatientV2ReviewSectionPayload) => void;
}

export const PatientReviewActions = ({ section, patient, value, onChange }: Props) => {
  const [pendingOpen, setPendingOpen] = useState(false);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [comments, setComments] = useState<Record<string, string>>({});
  const [error, setError] = useState('');
  const config = sectionConfig(section);
  const pendencies = useMemo<PatientV2ReviewPendencyPayload[]>(
    () => config.fields.filter((item) => selected[item.field]).map((item) => ({ field: item.field, label: item.label, comment: String(comments[item.field] ?? '').trim() })),
    [comments, config.fields, selected]
  );
  const openPending = () => {
    const current = value?.pendencies ?? [];
    setSelected(Object.fromEntries(current.map((item) => [item.field, true])));
    setComments(Object.fromEntries(current.map((item) => [item.field, item.comment])));
    setError('');
    setPendingOpen(true);
  };
  const savePending = () => {
    if (pendencies.length === 0 || pendencies.some((item) => !item.comment)) {
      setError('Selecione ao menos um campo e informe um comentário para cada pendência.');
      return;
    }
    onChange({ decision: 'pending', pendencies });
    setPendingOpen(false);
  };

  if (value?.decision === 'approved')
    return (
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Chip icon={<CheckCircleOutline />} label="Aprovado" size="small" sx={{ color: '#287a20', backgroundColor: '#eaf6e6', fontWeight: 700 }} />
        <Button size="small" onClick={() => onChange(undefined)} startIcon={<RestartAlt />} sx={{ textTransform: 'none' }}>
          Cancelar
        </Button>
      </Stack>
    );
  if (value?.decision === 'pending')
    return (
      <>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Chip icon={<WarningAmberOutlined />} label="Pendente" size="small" sx={{ color: '#9a5b00', backgroundColor: '#fff4dc', fontWeight: 700 }} />
          <Button size="small" onClick={openPending} startIcon={<EditOutlined />} sx={{ textTransform: 'none' }}>
            Alterar
          </Button>
          <Button size="small" onClick={() => onChange(undefined)} startIcon={<RestartAlt />} sx={{ textTransform: 'none' }}>
            Cancelar
          </Button>
        </Stack>
        <Dialog open={pendingOpen} onClose={() => setPendingOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Registrar pendências — {config.label}</DialogTitle>
          <DialogContent dividers>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <Stack spacing={1.25}>
              {config.fields.map((item) => {
                const checked = Boolean(selected[item.field]);
                return (
                  <Box key={item.field} sx={{ p: 1.25, border: '1px solid #e3e8e4', borderRadius: 1.5 }}>
                    <FormControlLabel
                      control={<Checkbox checked={checked} onChange={(_, checkedValue) => setSelected((current) => ({ ...current, [item.field]: checkedValue }))} />}
                      label={
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {item.label}{' '}
                          <Typography component="span" variant="caption" color="text.secondary">
                            ({item.value(patient) || 'não informado'})
                          </Typography>
                        </Typography>
                      }
                    />
                    {checked && (
                      <TextField
                        fullWidth
                        required
                        multiline
                        minRows={2}
                        size="small"
                        value={comments[item.field] ?? ''}
                        onChange={(event) => setComments((current) => ({ ...current, [item.field]: event.target.value }))}
                        placeholder={`Descreva a pendência de ${item.label.toLowerCase()}`}
                        sx={{ mt: 0.5 }}
                      />
                    )}
                  </Box>
                );
              })}
            </Stack>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2">Prévia da mensagem</Typography>
            <Box component="pre" sx={{ m: 0, mt: 0.75, p: 1.5, whiteSpace: 'pre-wrap', backgroundColor: '#f8faf8', borderRadius: 1.5, fontFamily: 'inherit', fontSize: 13 }}>
              {pendencies.length
                ? [`${config.label}:`, ...pendencies.map((item) => `- ${item.label}: ${item.comment || 'Comentário obrigatório'}`)].join('\n')
                : 'Selecione ao menos um campo.'}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPendingOpen(false)}>Cancelar</Button>
            <Button variant="contained" onClick={savePending} sx={{ backgroundColor: '#9a5b00', '&:hover': { backgroundColor: '#744500' } }}>
              Salvar pendências
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );

  return (
    <>
      <Stack direction="row" spacing={0.75} justifyContent="flex-end">
        <Button
          size="small"
          variant="contained"
          startIcon={<WarningAmberOutlined />}
          onClick={openPending}
          sx={{ color: '#fff', backgroundColor: '#b7791f', '&:hover': { backgroundColor: '#8c5a14' }, textTransform: 'none' }}
        >
          Registrar pendências
        </Button>
        <Button
          size="small"
          variant="contained"
          startIcon={<CheckCircleOutline />}
          onClick={() => onChange({ decision: 'approved' })}
          sx={{ backgroundColor: BrighterGreen, '&:hover': { backgroundColor: DarkGreen }, textTransform: 'none' }}
        >
          Aprovar seção
        </Button>
      </Stack>
      <Dialog open={pendingOpen} onClose={() => setPendingOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Registrar pendências — {config.label}</DialogTitle>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Stack spacing={1.25}>
            {config.fields.map((item) => {
              const checked = Boolean(selected[item.field]);
              return (
                <Box key={item.field} sx={{ p: 1.25, border: '1px solid #e3e8e4', borderRadius: 1.5 }}>
                  <FormControlLabel
                    control={<Checkbox checked={checked} onChange={(_, checkedValue) => setSelected((current) => ({ ...current, [item.field]: checkedValue }))} />}
                    label={
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {item.label}{' '}
                        <Typography component="span" variant="caption" color="text.secondary">
                          ({item.value(patient) || 'não informado'})
                        </Typography>
                      </Typography>
                    }
                  />
                  {checked && (
                    <TextField
                      fullWidth
                      required
                      multiline
                      minRows={2}
                      size="small"
                      value={comments[item.field] ?? ''}
                      onChange={(event) => setComments((current) => ({ ...current, [item.field]: event.target.value }))}
                      placeholder={`Descreva a pendência de ${item.label.toLowerCase()}`}
                      sx={{ mt: 0.5 }}
                    />
                  )}
                </Box>
              );
            })}
          </Stack>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2">Prévia da mensagem</Typography>
          <Box component="pre" sx={{ m: 0, mt: 0.75, p: 1.5, whiteSpace: 'pre-wrap', backgroundColor: '#f8faf8', borderRadius: 1.5, fontFamily: 'inherit', fontSize: 13 }}>
            {pendencies.length
              ? [`${config.label}:`, ...pendencies.map((item) => `- ${item.label}: ${item.comment || 'Comentário obrigatório'}`)].join('\n')
              : 'Selecione ao menos um campo.'}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={savePending} sx={{ backgroundColor: '#9a5b00', '&:hover': { backgroundColor: '#744500' } }}>
            Salvar pendências
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
